export type LoginContentLayout = 'none' | 'classic' | 'card' | 'full-bleed' | 'showcase' | 'custom';

export type LoginContentVariantKey = 'ehr' | 'essentials' | 'unknown' | 'test';

export type LoginContentVariant = {
  layout: LoginContentLayout;
  image: string;
  title: string;
  subtitle: string;
  content: string;
  buttonText: string;
  buttonLink: string;
  /** Primary background color for the marketing panel (hex). */
  backgroundColor: string;
  /** Accent color used for buttons, subtitle, highlights (hex). */
  accentColor: string;
  /** Show a frosted glass card behind the text content for readability. */
  textCard?: boolean;
  /** Raw HTML for the custom layout. Tailwind classes are available. */
  customHtml?: string;
};

export type LoginContent = Record<LoginContentVariantKey, LoginContentVariant>;

export const DEFAULT_LOGIN_CONTENT: LoginContent = {
  ehr: {
    layout: 'none',
    image: '',
    title: 'Welcome Back',
    subtitle: 'Your patients are waiting',
    content:
      'Access your full EHR suite — documentation, billing, and patient management all in one place.',
    buttonText: 'Learn More',
    buttonLink: 'https://chiroup.com/ehr',
    backgroundColor: '#1a2e12',
    accentColor: '#77BD43',
  },
  essentials: {
    layout: 'none',
    image: '',
    title: 'Welcome Back',
    subtitle: 'Evidence-based care at your fingertips',
    content:
      'Explore clinical resources, exercises, and treatment guides designed for modern chiropractic practice.',
    buttonText: 'Explore Features',
    buttonLink: 'https://chiroup.com',
    backgroundColor: '#f5faf0',
    accentColor: '#77BD43',
  },
  unknown: {
    layout: 'none',
    image: '',
    title: 'Modern Chiropractic Software',
    subtitle: 'Built by chiropractors, for chiropractors',
    content:
      'Streamline your practice with evidence-based clinical decision support, documentation, and billing tools.',
    buttonText: 'Start Free Trial',
    buttonLink: 'https://chiroup.com/pricing',
    backgroundColor: '#77BD43',
    accentColor: '#F49F01',
  },
  test: {
    layout: 'none',
    image: '',
    title: 'Test Environment',
    subtitle: 'Internal testing only',
    content: 'This login variant is only visible to clinic ID 27 for testing purposes.',
    buttonText: '',
    buttonLink: '',
    backgroundColor: '#1a2e12',
    accentColor: '#77BD43',
  },
};

export const CUSTOM_HTML_EXAMPLE = `<style>
  .panel {
    display: flex;
    flex: 1;
    flex-direction: column;
    justify-content: center;
    padding: 3rem;
    background: linear-gradient(135deg, #1e293b 0%, #0f172a 100%);
    color: #fff;
    font-family: system-ui, -apple-system, sans-serif;
    position: relative;
    overflow: hidden;
  }

  .panel::before,
  .panel::after {
    content: '';
    position: absolute;
    border-radius: 50%;
    opacity: 0.06;
    background: #77BD43;
  }

  .panel::before {
    width: 400px;
    height: 400px;
    top: -100px;
    right: -100px;
  }

  .panel::after {
    width: 300px;
    height: 300px;
    bottom: -60px;
    left: -60px;
  }

  .content { position: relative; z-index: 1; max-width: 480px; }

  .badge {
    display: inline-block;
    padding: 4px 12px;
    background: rgba(119, 189, 67, 0.2);
    color: #93cc68;
    font-size: 12px;
    font-weight: 500;
    border-radius: 999px;
    margin-bottom: 16px;
  }

  h1 {
    font-size: 2.5rem;
    font-weight: 800;
    line-height: 1.15;
    margin: 0 0 12px;
  }

  .subtitle {
    font-size: 1.1rem;
    color: rgba(255, 255, 255, 0.6);
    line-height: 1.6;
    margin: 0 0 24px;
  }

  .buttons { display: flex; gap: 12px; }

  .btn {
    display: inline-block;
    padding: 10px 20px;
    font-size: 14px;
    font-weight: 500;
    border-radius: 8px;
    text-decoration: none;
    transition: background 0.2s;
  }

  .btn-primary {
    background: #77BD43;
    color: #fff;
  }

  .btn-primary:hover { background: #477128; }

  .btn-outline {
    border: 1px solid rgba(255, 255, 255, 0.2);
    color: rgba(255, 255, 255, 0.8);
    background: transparent;
  }

  .btn-outline:hover { background: rgba(255, 255, 255, 0.1); }
</style>

<div class="panel">
  <div class="content">
    <span class="badge">New Feature</span>
    <h1>Evidence-Based Care,<br/>Simplified.</h1>
    <p class="subtitle">
      Access clinical decision support, streamlined documentation,
      and integrated billing — all in one platform.
    </p>
    <div class="buttons">
      <a href="https://chiroup.com/pricing" target="_blank" rel="noreferrer" class="btn btn-primary">
        Get Started
      </a>
      <a href="https://chiroup.com" target="_blank" rel="noreferrer" class="btn btn-outline">
        Learn More
      </a>
    </div>
  </div>
</div>`;

export const LOGIN_VARIANT_STORAGE_KEY = 'chiroup:loginVariant';
export const LOGIN_CLINIC_ID_STORAGE_KEY = 'chiroup:loginClinicId';
export const TEST_VARIANT_CLINIC_ID = 27;
